import moment from 'moment';
import React, { useContext, useEffect, useState } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import styled from 'styled-components';

import { colors, professionOptions, shareStudyViaOptions, utils } from '../../../core';
import Button from '../../ui/Button';
import CheckboxInput from '../../ui/CheckboxInput';
import Form from '../../ui/Form';
import Icon from '../../ui/Icon';
import LinkButton from '../../ui/LinkButton';
import RadioGroup from '../../ui/RadioGroup';
import SelectInput from '../../ui/SelectInput';
import Text from '../../ui/Text';
import TextInput from '../../ui/TextInput';
import { PractitionerLicense } from '../types';
import PractitionerFormContext from './PractitionerFormContext';

interface PractitionerInputsProps {
  emailIsVerified: boolean;
  setEmailIsVerified: (value: boolean) => void;
}

const AddButton = styled(LinkButton).attrs({
  type: 'button',
})`
  margin-top: -20px;
  margin-bottom: 32px;
`;

const RemoveButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: -20px;
`;

const RemoveButton = styled(LinkButton)`
  font-size: 12px;
`;

const CheckEmailContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  width: 80vw;

  & > * {
    margin-top: 12px;
    margin-right: 15px;
  }
`;

const PractitionerInputs = ({ emailIsVerified, setEmailIsVerified }: PractitionerInputsProps) => {
  const [verifyEmail, setVerifyEmail] = useState('');
  const { control, getValues, watch, setValue, setError, clearErrors } = useFormContext();

  const { disabledFields, editing, onCheckEmail, auth0InfoToFill, showCheckEmail } =
    useContext(PractitionerFormContext);

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'licenses',
  });

  const checkDisabled = (fieldName: string) => {
    const [first, ...name] = fieldName.split('.');

    if (!name.length) {
      return disabledFields?.some((item) => item === first);
    }

    return disabledFields?.some((item) => item === name.join('.'));
  };

  const addNewLicense = () => {
    append({
      licenseJurisdiction: '',
      licenseNumber: '',
      licenseType: '',
    });
  };

  const isEmailPlaceholderCheck = watch('placeholderEmail');
  const emailValue = watch('emailAddress');
  const firstNameValue = watch('firstName');
  const lastNameValue = watch('lastName');

  useEffect(() => {
    if (isEmailPlaceholderCheck) {
      if (firstNameValue && lastNameValue) {
        const placeholderEmail = `HP+${firstNameValue
          .replaceAll(' ', '_')
          .toLowerCase()}.${lastNameValue.replaceAll(' ', '_').toLowerCase()}+${moment().format(
          'YY-DD-MM'
        )}@prenuvo.com`;

        setValue('emailAddress', placeholderEmail);
        setVerifyEmail(placeholderEmail);
      } else {
        if (!firstNameValue) {
          setVerifyEmail('');
          setError('firstName', { message: 'This is required.' });
        }

        if (!lastNameValue) {
          setVerifyEmail('');
          setError('lastName', { message: 'This is required.' });
        }
      }
    }
  }, [
    isEmailPlaceholderCheck,
    firstNameValue,
    lastNameValue,
    setValue,
    setError,
    clearErrors,
    setEmailIsVerified,
    setVerifyEmail,
  ]);

  const handleCheckEmail = async () => {
    if (onCheckEmail) {
      if (emailValue) {
        const verificationResult = await onCheckEmail(emailValue);
        setEmailIsVerified(verificationResult);
        if (verificationResult) {
          setVerifyEmail(emailValue);
        }
      } else {
        setVerifyEmail('');
        setError('emailAddress', { shouldFocus: true, message: 'This is required.' });
      }
    }
  };

  useEffect(() => {
    if (!!auth0InfoToFill?.firstname) {
      setEmailIsVerified(true);
      setValue('firstName', auth0InfoToFill.firstname);
      setValue('lastName', auth0InfoToFill.lastname);
      setValue('phoneNumber', auth0InfoToFill.phoneNumber);
    }
  }, [auth0InfoToFill, emailValue, setValue, setEmailIsVerified]);

  useEffect(() => {
    if (!showCheckEmail) setEmailIsVerified(true);
  }, [showCheckEmail, setEmailIsVerified]);

  useEffect(() => {
    if (emailIsVerified && emailValue !== verifyEmail) {
      setEmailIsVerified(false);
      setValue('placeholderEmail', false);
    }
  }, [emailValue, setEmailIsVerified, setValue]);

  return (
    <React.Fragment>
      <Form.RowLabel>Practitioner Contact Information</Form.RowLabel>
      <Form.Row>
        <div>
          <Form.Controller
            as={TextInput}
            type="email"
            placeholder="Email"
            label="Email"
            name="emailAddress"
            rules={utils.validate('emailAddress', 'required')}
            disabled={checkDisabled('emailAddress')}
          />
          {showCheckEmail || editing ? (
            <CheckEmailContainer>
              <Form.Controller
                as={''}
                name={`placeholderEmail`}
                render={(props: any) => {
                  return (
                    <CheckboxInput
                      onChange={(e: any) => {
                        if (e) {
                          if (!e.target.checked && emailIsVerified) {
                            setEmailIsVerified(false);
                          }
                          setValue('placeholderEmail', e.target.checked);
                        }
                      }}
                      checked={
                        getValues('placeholderEmail') ? !!getValues('placeholderEmail') : false
                      }
                      name={props.name}
                      id={props.name}
                      label="Use placeholder email address"
                      disabled={checkDisabled('emailAddress')}
                    />
                  );
                }}
              />
              <Text noMargin size="small">
                Please click the button below to make sure the email address is not already in the
                system
              </Text>
              <Button
                onClick={handleCheckEmail}
                variant="secondary"
                size="xsmall"
                disabled={checkDisabled('emailAddress')}
              >
                {emailIsVerified ? <Icon type="complete" color={colors.primary} /> : 'Check Email'}
              </Button>
            </CheckEmailContainer>
          ) : null}
        </div>

        <Form.Controller
          as={TextInput}
          type="tel"
          placeholder="Phone Number"
          label="Phone Number"
          name="phoneNumber"
          rules={utils.validate('phoneNumber')}
          disabled={checkDisabled('phoneNumber')}
        />

        <Form.Controller
          as={TextInput}
          type="tel"
          placeholder="Fax Number"
          label="Fax Number"
          name="faxNumber"
          rules={utils.validate('faxNumber')}
          disabled={checkDisabled('faxNumber')}
        />
      </Form.Row>

      <Form.RowLabel>Practitioner Personal Information</Form.RowLabel>
      <Form.Row>
        <Form.Controller
          as={TextInput}
          type="text"
          placeholder="Salutation"
          name="salutation"
          label="Salutation"
          rules={utils.validate('salutation')}
          disabled={checkDisabled('salutation')}
        />

        <Form.Controller
          as={TextInput}
          type="text"
          placeholder="First Name"
          name="firstName"
          label="First Name"
          rules={utils.validate('firstName', 'required')}
          disabled={checkDisabled('firstName')}
        />

        <Form.Controller
          as={TextInput}
          type="text"
          placeholder="Last Name"
          label="Last Name"
          name="lastName"
          rules={utils.validate('lastName', 'required')}
          disabled={checkDisabled('lastName')}
        />

        <Form.Controller
          as={SelectInput}
          variant="primary"
          name="profession"
          options={professionOptions}
          label="Profession"
          placeholder={'Practitioner Profession'}
          rules={utils.validate('profession', 'required')}
          disabled={checkDisabled('profession')}
        />

        <Form.Controller
          as={TextInput}
          type="text"
          placeholder="Title"
          label="Title"
          name="title"
          rules={utils.validate('title')}
          disabled={checkDisabled('title')}
        />

        <Form.Controller
          as={SelectInput}
          variant="primary"
          name="shareMethod"
          options={shareStudyViaOptions}
          label="Preferred Share Method"
          placeholder={'Preferred Share Method'}
          rules={utils.validate('shareMethod')}
          disabled={checkDisabled('shareMethod')}
        />

        <Form.Controller
          as={TextInput}
          type="text"
          placeholder="MSP #"
          label="MSP #"
          name="mspNumber"
          rules={utils.validate('mspNumber')}
          disabled={checkDisabled('mspNumber')}
        />

        <Form.Controller
          as={TextInput}
          type="text"
          placeholder="Designation"
          label="Designation"
          name="designation"
          rules={utils.validate('designation')}
          disabled={checkDisabled('designation')}
        />
      </Form.Row>

      <Form.RowLabel>Practitioner ID/Licenses</Form.RowLabel>

      {fields.map((field: PractitionerLicense, index: number) => (
        <div key={field.id || index}>
          <Form.Row>
            <Form.Controller
              as={TextInput}
              type="text"
              name={`licenses[${index}].licenseJurisdiction`}
              label="ID/License Jurisdiction"
              placeholder={'ID/License Jurisdiction'}
              defaultValue={field.licenseJurisdiction}
              disabled={checkDisabled(`licenses[${index}].licenseJurisdiction`)}
            />

            <Form.Controller
              as={TextInput}
              type="text"
              placeholder="Practitioner ID/License Type"
              name={`licenses[${index}].licenseType`}
              label="Practitioner ID/License Type"
              defaultValue={field.licenseType}
              disabled={checkDisabled(`licenses[${index}].licenseType`)}
            />

            <Form.Controller
              as={TextInput}
              type="text"
              placeholder="Practitioner ID/License Number"
              label="Practitioner ID/License Number"
              name={`licenses[${index}].licenseNumber`}
              defaultValue={field.licenseNumber}
              disabled={checkDisabled(`licenses[${index}].licenseNumber`)}
            />
          </Form.Row>

          {editing && (
            <RemoveButtonContainer>
              <RemoveButton
                onClick={() => remove(index)}
                iconRight="error"
                iconColor={colors.primary}
              >
                Remove
              </RemoveButton>
            </RemoveButtonContainer>
          )}
        </div>
      ))}

      {editing && (
        <AddButton onClick={addNewLicense} iconLeft="add" iconSize={20}>
          Add New License for this Practitioner
        </AddButton>
      )}

      <Form.RowLabel>Practitioner Status</Form.RowLabel>

      <Form.Row>
        <Form.Controller
          as={''}
          name="deactivated"
          label="Deactivated"
          render={(props: any) => {
            return (
              <RadioGroup
                name={props.name}
                onChange={props.onChange}
                initialValue={`${props.value}`}
                title={utils.capitalize(props.name)}
              >
                <RadioGroup.Input
                  id={`${props.name}1`}
                  label="True"
                  value="true"
                  disabled={checkDisabled(`deactivated`)}
                />

                <RadioGroup.Input
                  id={`${props.name}2`}
                  label="False"
                  value="false"
                  disabled={checkDisabled(`deactivated`)}
                />
              </RadioGroup>
            );
          }}
        />

        <Form.Controller
          as={''}
          name="verified"
          label="Verified"
          render={(props: any) => {
            return (
              <RadioGroup
                name={props.name}
                onChange={props.onChange}
                initialValue={`${props.value}`}
                title={utils.capitalize(props.name)}
              >
                <RadioGroup.Input
                  id={`${props.name}1`}
                  label="True"
                  value="true"
                  disabled={checkDisabled(`verified`)}
                />

                <RadioGroup.Input
                  id={`${props.name}2`}
                  label="False"
                  value="false"
                  disabled={checkDisabled(`verified`)}
                />
              </RadioGroup>
            );
          }}
        />
      </Form.Row>
    </React.Fragment>
  );
};

export default PractitionerInputs;
